import { useCanGoBack, useRouter } from '@tanstack/react-router'
import type { ReactNode } from 'react'

export type TopBarProps = {
  title: ReactNode
  sub?: ReactNode
  left?: ReactNode
  right?: ReactNode
  big?: boolean
}

export const TopBar = ({ title, sub, left, right, big }: TopBarProps) => (
  <div
    style={{
      padding: big ? '14px 18px 12px' : '10px 14px',
      borderBottom: '1px solid var(--line-soft)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      background: 'var(--paper)',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {left}
      <div>
        <div
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: big ? 24 : 17,
            fontWeight: 700,
            letterSpacing: -0.4,
            color: 'var(--ink)',
          }}
        >
          {title}
        </div>
        {sub && <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{sub}</div>}
      </div>
    </div>
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>{right}</div>
  </div>
)

export type BackButtonProps = {
  /**
   * Where to go when there is no in-app history to pop — i.e. the user
   * deep-linked or reloaded straight onto this screen. This is the
   * screen's hierarchical parent (the "Up" target). When history *does*
   * exist, the arrow pops it instead, so it behaves as a true "Back".
   */
  fallback: () => void
}

export const BackButton = ({ fallback }: BackButtonProps) => {
  const router = useRouter()
  const canGoBack = useCanGoBack()
  return (
    <button
      type="button"
      aria-label="Back"
      onClick={() => (canGoBack ? router.history.back() : fallback())}
      style={{
        width: 32,
        height: 32,
        borderRadius: 16,
        border: '1px solid var(--line-soft)',
        background: 'var(--paper)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 16,
        color: 'var(--ink)',
        padding: 0,
        cursor: 'pointer',
      }}
    >
      ←
    </button>
  )
}
